import React, { useState } from 'react';
import { makeStyles, createStyles, Tooltip, IconButton } from '@material-ui/core';
import { mainTheme } from '../../../Theme';
import CheckIcon from '@material-ui/icons/Check';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CallOutlinedIcon from '@material-ui/icons/CallOutlined';
import AddIcon from '@material-ui/icons/Add';

// Explicit request: AU mobiles ("0455 852 159") and landlines ("02 9876 5432") read as one
// unbroken run of digits otherwise - grouped for readability, display-only (the tel: link and
// the clipboard copy both still use the raw, ungrouped value). Only recognises the two common AU
// shapes this app's own data actually uses; anything else (international numbers, odd lengths)
// is returned unchanged rather than guessed at.
export const formatAuPhoneForDisplay = (rawPhone: string): string => {
    const digitsOnly = rawPhone.replace(/\D/g, '');
    if (digitsOnly.length === 10 && digitsOnly.startsWith('04')) {
        return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7)}`;
    }
    if (digitsOnly.length === 10 && /^0[2378]/.test(digitsOnly)) {
        return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 6)} ${digitsOnly.slice(6)}`;
    }
    return rawPhone;
};

const useContactLineStyles = makeStyles(() =>
    createStyles({
        root: {
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            minWidth: 0,
            '&:hover $actions, &:focus-within $actions': {
                opacity: 1,
                pointerEvents: 'auto',
            },
        },
        // Explicit request: neutral ink by default - blue + underline is reserved for
        // hover/focus, rather than every contact line reading as a link all the time. gap:8 and
        // minHeight:28 are shared with AddContactFieldLink's own row below, so a line's text
        // left edge lands in the same place whether the field is filled or shown as "Add ‹field›".
        link: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 24,
            minWidth: 0,
            overflow: 'hidden',
            fontSize: '13px',
            color: mainTheme.TextColor.Primary,
            textDecoration: 'none',
            '& span': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
            '&:hover, &:focus': {
                color: mainTheme.TemplateColor.Primary,
                textDecoration: 'underline',
            },
        },
        // Explicit request: only visible on hover/focus of the whole line (root's own
        // &:hover/&:focus-within above) - opacity+pointerEvents rather than conditional
        // rendering, so a screen reader / keyboard user tabbing to the line's own icon buttons
        // doesn't depend on a mouse hover state that never fired for them (focus-within covers
        // that case here).
        actions: {
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.1s ease',
        },
        actionButton: {
            padding: 3,
            '& svg': {
                fontSize: 14,
            },
        },
    }),
);

interface ContactLineProps {
    icon: React.ReactNode;
    href: string;
    displayText: string;
    copyText: string;
    fullText: string;
    showCall?: boolean;
}

// Explicit request: copy (every line) + call (phone/mobile only) icon buttons, revealed on
// hover/focus of the line. No SMS button - no enquiry-compatible send-SMS action exists anywhere
// in the app today (SendSMSDialog.tsx is built specifically for Name/Contact records, a
// different guid/template shape) - building that integration is out of scope here. Same
// clipboard pattern/timing as HistoryActivity.tsx's own copy button (ExpandableNote) -
// navigator.clipboard.writeText, 1.5s "Copied" confirmation, icon swap.
export const ContactLine: React.FC<ContactLineProps> = ({ icon, href, displayText, copyText, fullText, showCall }) => {
    const classes = useContactLineStyles();
    const [isCopied, setIsCopied] = useState(false);

    const onCopy = () => {
        navigator.clipboard.writeText(copyText)
            .then(() => {
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 1500);
            });
    };

    return (
        <div className={classes.root}>
            <a className={classes.link} href={href} title={fullText}>
                {icon}
                <span>{displayText}</span>
            </a>
            <span className={classes.actions}>
                <Tooltip title={isCopied ? 'Copied' : 'Copy'}>
                    <IconButton
                        size="small"
                        className={classes.actionButton}
                        aria-label={isCopied ? 'Copied' : `Copy ${fullText}`}
                        onClick={onCopy}
                    >
                        {isCopied ? <CheckIcon fontSize="small" /> : <FileCopyOutlinedIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
                {showCall && (
                    <Tooltip title="Call">
                        <IconButton
                            size="small"
                            className={classes.actionButton}
                            aria-label={`Call ${fullText}`}
                            href={href}
                        >
                            <CallOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </span>
        </div>
    );
};

export const useAddContactFieldLinkStyles = makeStyles(() =>
    createStyles({
        // Explicit request: same leading-icon-column structure as a filled ContactLine, and the
        // same gap:8/minHeight:28 as ContactLine's own link class, so the button text's left
        // edge lines up exactly with a filled line's text, whichever renders for a given field.
        // Icon colour is inherited (muted) from here rather than set per-icon.
        row: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 24,
            color: mainTheme.TextColor.Secondary,
            '& > svg:first-child': {
                fontSize: 15,
            },
        },
        // Explicit request: dashed underline signals "empty, add something" (reusing the same
        // neutral/muted colour language the Due step's own dashed "not yet happened" ring
        // already uses elsewhere in this header) - solid blue only once the user is actually
        // interacting with it (hover/focus).
        button: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '0 0 1px',
            border: 0,
            borderBottom: `1px dashed ${mainTheme.CommonColor.LightGrey2}`,
            background: 'none',
            font: 'inherit',
            fontSize: '13.5px',
            fontStyle: 'normal',
            fontWeight: 400,
            color: mainTheme.TextColor.Secondary,
            cursor: 'pointer',
            '&:hover, &:focus-visible': {
                color: mainTheme.TemplateColor.Primary,
                borderBottomColor: mainTheme.TemplateColor.Primary,
            },
            '&:focus-visible': {
                outline: `2px solid ${mainTheme.TemplateColor.Primary}`,
                outlineOffset: 2,
            },
        },
        plusIcon: {
            fontSize: 13,
        },
        // Explicit follow-up request: variant for sitting inline inside a coloured warning sentence
        // (the Send SMS "requires a mobile number" notice) - takes that sentence's own size/colour
        // instead of the header row's, and underlines in that same colour. The nested svg rule
        // ties with noEmailMeetingWrapper's own '& svg' (16px + marginTop:1) on specificity and
        // wins on sheet order, so the "+" stays small and on the text baseline.
        inlineButton: {
            fontSize: 'inherit',
            fontWeight: 600,
            color: 'inherit',
            verticalAlign: 'baseline',
            borderBottomColor: 'currentColor',
            '& svg': {
                fontSize: 14,
                marginTop: 0,
            },
            '&:hover, &:focus-visible': {
                color: 'inherit',
                borderBottomStyle: 'solid',
            },
        },
    }),
);

interface AddContactFieldLinkProps {
    icon: React.ReactNode;
    label: string;
    ariaLabel: string;
    onClick: () => void;
}

// Explicit request: the empty-state counterpart to ContactLine above - same row structure
// (leading icon, then content), a dashed-underline button rather than italic plain text, with a
// small "+" icon ahead of the label.
export const AddContactFieldLink: React.FC<AddContactFieldLinkProps> = ({ icon, label, ariaLabel, onClick }) => {
    const classes = useAddContactFieldLinkStyles();
    return (
        <div className={classes.row}>
            {icon}
            <button type="button" className={classes.button} onClick={onClick} aria-label={ariaLabel}>
                <AddIcon className={classes.plusIcon} />
                {label}
            </button>
        </div>
    );
};
